"use client";

import { Archive, Minus, Plus, Trash2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

import { Dialog } from "@/components/shared/Dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { Ingredient } from "@/lib/types";
import { ingredientSchemas, validate } from "@/lib/validation";

interface EditIngredientModalProps {
  isOpen: boolean;
  onClose: () => void;
  ingredient: Ingredient;
  onSave: (newText: string) => void;
  onArchive: () => void;
  onDelete: () => void;
  onIncrementQuantity?: () => void;
  onDecrementQuantity?: () => void;
  archiveButtonText?: string;
}

export function EditIngredientModal({
  isOpen,
  onClose,
  ingredient,
  onSave,
  onArchive,
  onDelete,
  onIncrementQuantity,
  onDecrementQuantity,
  archiveButtonText,
}: EditIngredientModalProps) {
  const t = useTranslations("ingredients");
  const [editText, setEditText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Update editText when ingredient changes or modal opens
  useEffect(() => {
    if (isOpen) {
      const fullText = ingredient.quantity
        ? `${ingredient.quantity} ${ingredient.text}`
        : ingredient.text;
      setEditText(fullText);
    }
  }, [isOpen, ingredient]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleBlur = () => {
    // Only save if text has changed
    const currentFullText = ingredient.quantity
      ? `${ingredient.quantity} ${ingredient.text}`
      : ingredient.text;

    if (editText.trim() && editText !== currentFullText) {
      // Validate before saving
      const validationResult = validate(ingredientSchemas.text, editText);
      if (validationResult.success) {
        onSave(editText);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  const handleIncrement = () => {
    if (onIncrementQuantity) {
      onIncrementQuantity();
      // Update local state with the new quantity
      // The parent will update the ingredient state, and useEffect will sync editText
    }
  };

  const handleDecrement = () => {
    if (onDecrementQuantity) {
      onDecrementQuantity();
      // Update local state with the new quantity
      // The parent will update the ingredient state, and useEffect will sync editText
    }
  };

  const handleArchive = () => {
    onArchive();
    onClose();
  };

  const handleDelete = () => {
    onDelete();
    onClose();
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title={t("editIngredient")}>
      <div className="space-y-4">
        {/* Input bar for editing ingredient name/quantity */}
        <div>
          <Input
            ref={inputRef}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            maxLength={200}
          />
        </div>

        {/* Quantity controls */}
        <div className="flex items-center gap-3">
          <span className="mr-auto">{t("quantity")}</span>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDecrement}
              disabled={!onDecrementQuantity}
              className="size-12 p-0 rounded-full"
              aria-label={t("decrementQuantity")}
            >
              <Minus className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleIncrement}
              disabled={!onIncrementQuantity}
              className="size-12 p-0 rounded-full"
              aria-label={t("incrementQuantity")}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={handleArchive}>
            <Archive className="size-4" />
            {archiveButtonText || t("archive")}
          </Button>
          <Button variant="destructive" className="flex-1" onClick={handleDelete}>
            <Trash2 className="size-4" />
            {t("delete")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
